import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import validationEngine from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AppInformationService, AuthService, GenesisUnitOfWork } from '../../core/core.module';

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    styleUrls: ['./user-detail.component.scss'],
    standalone: false
})
export class UserDetailComponent implements OnInit {
    key: any;
    entity: any;
    roles: any;
    currentRoleIds: number[];
    selectedRoleIds: number[];
    validationGroup = 'userValidationGroup';
    errorAction = 'saving';

    constructor(
        private uow: GenesisUnitOfWork,
        private auth: AuthService,
        private route: ActivatedRoute,
        protected location: Location,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        private router: Router
    ) { }

    async ngOnInit() {
        this.roles = this.uow.lookups.roles;

        this.validateUsername = this.validateUsername.bind(this);
        this.validatePassword = this.validatePassword.bind(this);
        this.validatePasswordRequired = this.validatePasswordRequired.bind(this);

        this.key = this.route.snapshot.params['id'];
        if (this.key === 'new') {
            this.entity = this.uow.users.createEntity();
            this.entity.active = true;
            this.selectedRoleIds = [];
            this.currentRoleIds = [];
        } else {
            const user = await this.uow.users.byId(this.key, 'userRoles, userRoles.role');
            user['newPassword'] = null; // need to clear out any password from previous edit
            this.entity = user;
            this.updateRoleIds();
        }
    }

    hasChanges() {
        return this.uow.hasChanges() || this.hasSelectionChanged || this.hasPasswordChanged;
    }

    hasSelectionChanged = false as boolean;
    onSelectionChanged(e) {
        this.hasSelectionChanged = true;
    }

    hasPasswordChanged = false as boolean;
    onValueChanged = (e: any) => {
        this.hasPasswordChanged = (e.value && e.value.trim() !== '' ? true : false);
    }

    async save() {
        if (!(await this.validate())) return;
        this.appInfo.pleaseWaitShow();

        const addedIds = _.difference(this.selectedRoleIds, this.currentRoleIds);
        const removedIds = _.difference(this.currentRoleIds, this.selectedRoleIds);

        addedIds.forEach(roleId => {
            this.uow.userRoles.createEntity({ roleId, user: this.entity });
        });

        removedIds.forEach(roleId => {
            const userRole = _(this.entity.userRoles).find({ roleId: roleId } as any) as any;
            if (userRole) {
                const indexOfUserRole = this.entity.userRoles.indexOf(userRole);
                this.entity.userRoles.splice(indexOfUserRole, 1);
                userRole.entityAspect.setDeleted();
            }
        });

        await this.uow.commit();
        this.updateRoleIds();
        if (this.entity.newPassword && this.entity.newPassword.trim() !== '') {
            await this.uow.changeUserPassword(this.entity, this.entity.newPassword.trim());
            this.hasPasswordChanged = false;
            this.entity.newPassword = null;
        }

        if (this.key === 'new') {
            this.router.navigate(['../' + this.entity.id], { relativeTo: this.route, replaceUrl: true, state: { navigateOnSaveNew: true } });
        }
        this.key = this.entity.id;
        this.appInfo.pleaseWaitHide();
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    updateRoleIds() {
        this.hasSelectionChanged = false;
        this.currentRoleIds = _(this.entity.userRoles)
            .sortBy('role.name')
            .map(ur => ur.roleId)
            .value();

        this.selectedRoleIds = this.currentRoleIds.slice();
    }

    async validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            const completePromise = result.complete as any;
            if (completePromise && result.status === 'pending') {
                const asyncResult = await completePromise;
                return this.isValidWithMessage(asyncResult);
            } else {
                return this.isValidWithMessage(result);
            }
        } else {
            return true;
        }
    }

    private isValidWithMessage(result) {
        if (!result.isValid) {
            let message = '';
            result.brokenRules.forEach((brokenRule) => {
                if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
            });
            this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
            return false;
        } else {
            return true;
        }
    }

    validateUsername = async (e) => {
        // check if this username is already used...
        const users = await this.uow.getUsers(null, { notId: this.entity.id, userName: e.value });
        return users.length === 0;
    }

    validatePasswordSpaces = (e) => {
        if (!e.value) {
            return true;
        } else {
            return (e.value.replaceAll(' ') === e.value);
        }
    }

    validatePassword = (e) => {
        if (!e.value || e.value.trim() === '') {
            return true;
        } else {
            return (e.value.trim().length >= 8);
        }
    }

    validatePasswordRequired = (e) => {
        return (this.entity.id > 0 || (!!e.value && e.value.trim() !== ''));
    }
}
